import { useCallback, useEffect, useRef } from "react";
import { plan_bottom_up_periods_path } from "@/lib/routes";
import type { DailyRow } from "../components/BottomUpDailyGrid";
import { toCellUpdates } from "./cellUpdates";

export interface CellUpdate {
  product_external_id: string;
  period_date: string;
  quantity: number;
  manual: boolean;
}

interface Args {
  marketSlug: string;
  planSlug: string;
  channelId: string;
  setLocalRows: React.Dispatch<React.SetStateAction<DailyRow[]>>;
  markSaved: (cells: { productExternalId: string; periodDate: string }[]) => void;
  markError: (cells: { productExternalId: string; periodDate: string }[]) => void;
}

const FLUSH_DELAY_MS = 400;

// Optimistically applies day-cell updates to local rows, queues them, and
// persists the queue in one batched PATCH after a short debounce — rapid
// typing across cells coalesces into a single request. Only rows that
// actually contain an update get a new object identity, so memoized rows
// don't re-render.
export function useCellSaver({ marketSlug, planSlug, channelId, setLocalRows, markSaved, markError }: Args) {
  // Queue keyed by cell so a re-edit before flush keeps only the last value.
  const queueRef = useRef<Map<string, CellUpdate>>(new Map());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const updates = Array.from(queueRef.current.values());
    queueRef.current = new Map();
    if (updates.length === 0) return;

    const cells = updates.map((u) => ({
      productExternalId: u.product_external_id,
      periodDate: u.period_date,
    }));
    const csrfToken =
      document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
    fetch(plan_bottom_up_periods_path(marketSlug, planSlug), {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({
        cells: updates.map((u) => ({
          ...u,
          granularity: "day",
          org_node_id: channelId,
        })),
      }),
    })
      .then((res) => (res.ok ? markSaved(cells) : markError(cells)))
      .catch(() => markError(cells));
  }, [marketSlug, planSlug, channelId, markSaved, markError]);

  const saveCells = useCallback(
    (updates: CellUpdate[], { immediate = false }: { immediate?: boolean } = {}) => {
      if (updates.length === 0) return;

      const byProduct = new Map<string, Map<string, CellUpdate>>();
      for (const u of updates) {
        queueRef.current.set(`${u.product_external_id}|${u.period_date}`, u);
        const perRow = byProduct.get(u.product_external_id) ?? new Map();
        perRow.set(u.period_date, u);
        byProduct.set(u.product_external_id, perRow);
      }

      setLocalRows((prev) =>
        prev.map((r) => {
          const perRow = byProduct.get(r.product_external_id);
          if (!perRow) return r;
          return {
            ...r,
            days: r.days.map((d) => {
              const u = perRow.get(d.period_date);
              return u === undefined ? d : { ...d, quantity: u.quantity, manual: u.manual };
            }),
          };
        }),
      );

      if (immediate) {
        flush();
      } else {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flush, FLUSH_DELAY_MS);
      }
    },
    [setLocalRows, flush],
  );

  // Don't leave queued edits behind on unmount (month/channel navigation).
  useEffect(() => flush, [flush]);

  const handlePasteCells = useCallback(
    (productExternalId: string, updates: { period_date: string; quantity: number | null }[]) =>
      saveCells(toCellUpdates(productExternalId, updates)),
    [saveCells],
  );

  const handleCellChange = useCallback(
    (productExternalId: string, periodDate: string, quantity: number | null) =>
      handlePasteCells(productExternalId, [{ period_date: periodDate, quantity }]),
    [handlePasteCells],
  );

  return { saveCells, handlePasteCells, handleCellChange };
}
